import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  MapPin, Calendar, Users, Shield, Award, FileCheck, Send, ArrowLeft,
  Building2, GraduationCap, BadgeCheck, Star, Clock, Flag, AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";
import Seo, { SITE_URL } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Stars, VerifiedBadge, LoadingScreen, inr } from "@/components/common";

const REPORT_REASONS = ["Incorrect information", "Misleading claims", "Outdated details", "Fake or suspicious profile", "Other"];

function Section({ title, children }) {
  return (
    <div className="bg-white border border-navy-200 rounded-xl p-6">
      <h3 className="font-heading font-semibold text-navy-900 mb-4">{title}</h3>
      {children}
    </div>
  );
}

function ReportDialog({ agencyId, agencyName }) {
  const [open, setOpen] = useState(false);
  const [r, setR] = useState({ reason: "", details: "", reporter_name: "", reporter_contact: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!r.reason) { toast.error("Please select a reason"); return; }
    setBusy(true);
    try {
      const res = await api.post(`/agencies/${agencyId}/report`, r);
      toast.success(res.data.message); setOpen(false); setR({ reason: "", details: "", reporter_name: "", reporter_contact: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit report"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="flex items-center gap-1.5 text-sm text-navy-400 hover:text-red-500 transition-colors" data-testid="report-profile-btn">
        <Flag size={14} /> Report this profile
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader><DialogTitle>Report {agencyName}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-navy-500">Help us keep GuardDekho accurate. Reports are reviewed by our team.</p>
            <div>
              <Label className="text-xs">Reason *</Label>
              <Select value={r.reason} onValueChange={(v) => setR({ ...r, reason: v })}>
                <SelectTrigger className="mt-1" data-testid="report-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>{REPORT_REASONS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Details</Label><Textarea rows={3} className="mt-1" placeholder="What's wrong with this profile?" value={r.details} onChange={(e) => setR({ ...r, details: e.target.value })} data-testid="report-details" /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Your Name (optional)</Label><Input className="mt-1" value={r.reporter_name} onChange={(e) => setR({ ...r, reporter_name: e.target.value })} data-testid="report-name" /></div>
              <div><Label className="text-xs">Your Contact (optional)</Label><Input className="mt-1" placeholder="Email or phone" value={r.reporter_contact} onChange={(e) => setR({ ...r, reporter_contact: e.target.value })} data-testid="report-contact" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-navy-300" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" disabled={busy} onClick={submit} data-testid="report-submit">Submit Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function AgencyProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);

  useEffect(() => { api.get(`/agencies/${id}`).then((r) => setA(r.data)).catch(() => {}); }, [id]);
  if (!a) return <LoadingScreen />;

  return (
    <div>
      <Seo
        title={`${a.name} — Security Agency${a.location ? ` in ${a.location}` : ""} | GuardDekho.com`}
        description={`${a.name} is a security agency${a.location ? ` in ${a.location}` : ""} listed on GuardDekho.com. View services, ratings, PSARA/GST compliance and request a quotation.`}
        canonical={`${SITE_URL}/agencies/${a.slug || id}`}
        type="profile"
        noindex={a.is_listed === false}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: a.name,
          url: `${SITE_URL}/agencies/${a.slug || id}`,
          address: a.location ? { "@type": "PostalAddress", addressLocality: a.location, addressCountry: "IN" } : undefined,
          aggregateRating: a.rating ? { "@type": "AggregateRating", ratingValue: a.rating, reviewCount: a.reviews_count || 1 } : undefined,
        }}
      />
      <section className="bg-navy-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-navy-300 hover:text-white text-sm mb-6"><ArrowLeft size={16} /> Back</button>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-20 h-20 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center font-heading text-3xl font-bold">{a.name[0]}</div>
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="font-heading text-3xl font-extrabold tracking-tight">{a.name}</h1>
                  {a.verified && <Badge className="bg-emerald-500 hover:bg-emerald-500 gap-1"><BadgeCheck size={14} /> Verified Agency</Badge>}
                </div>
                <div className="flex items-center gap-4 mt-2 text-navy-300 text-sm flex-wrap">
                  <span className="flex items-center gap-1"><MapPin size={14} /> {a.head_office?.city}, {a.head_office?.state}</span>
                  <span className="flex items-center gap-1"><Calendar size={14} /> Est. {a.established_year}</span>
                  <span className="flex items-center gap-1"><Stars value={a.rating} /> {a.rating}</span>
                </div>
              </div>
            </div>
            <Button className="bg-brand hover:bg-brand-600 h-12 px-8" onClick={() => navigate("/post-requirement")} data-testid="profile-request-quote"><Send size={16} className="mr-2" /> Request Quote</Button>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[["Total Guards", a.total_guards], ["Contracts", a.contracts_completed], ["Response Rate", `${a.response_rate}%`], ["From", `${inr(a.price_per_guard_month)}/mo`]].map(([l, v]) => (
              <div key={l} className="bg-white/5 border border-white/10 rounded-xl p-4"><div className="font-heading text-2xl font-bold">{v}</div><div className="brand-label text-navy-400 mt-1">{l}</div></div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {a.under_review && (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="agency-under-review">
              <AlertTriangle size={20} className="text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-heading font-semibold text-amber-800">Under Review</p>
                <p className="text-sm text-amber-700">This profile has received multiple reports and is currently being reviewed by the GuardDekho team. Please exercise due diligence.</p>
              </div>
            </div>
          )}
          <Section title="Company Overview"><p className="text-navy-600 leading-relaxed">{a.description}</p></Section>
          <Section title="Services Offered">
            <div className="flex flex-wrap gap-2">{a.services?.map((s) => <span key={s} className="px-3 py-1.5 rounded-lg bg-navy-100 text-navy-700 text-sm font-medium">{s}</span>)}</div>
          </Section>
          <div className="grid sm:grid-cols-2 gap-6">
            <Section title="Industries Served"><ul className="space-y-2">{a.industries?.map((i) => <li key={i} className="flex items-center gap-2 text-navy-600"><Building2 size={15} className="text-brand" /> {i}</li>)}</ul></Section>
            <Section title="Service Locations"><ul className="space-y-2">{a.service_locations?.map((i) => <li key={i} className="flex items-center gap-2 text-navy-600"><MapPin size={15} className="text-brand" /> {i}</li>)}</ul></Section>
          </div>
          <Section title="Certifications & Training">
            <div className="flex flex-wrap gap-2 mb-3">{a.certifications?.map((c) => <Badge key={c} variant="outline" className="border-navy-300 text-navy-700 gap-1"><Award size={12} /> {c}</Badge>)}</div>
            <p className="text-sm text-navy-600 flex items-start gap-2"><GraduationCap size={16} className="text-brand mt-0.5" /> {a.training_standards}</p>
          </Section>
          <Section title={`Reviews (${a.reviews?.length || 0})`}>
            {(!a.reviews || a.reviews.length === 0) ? <p className="text-navy-500 text-sm">No verified reviews yet.</p> : (
              <div className="space-y-4">
                {a.reviews.map((r) => (
                  <div key={r.id} className="border-b border-navy-100 pb-4 last:border-0">
                    <div className="flex items-center justify-between"><span className="font-semibold text-navy-800">{r.customer_name}</span><Stars value={r.overall} /></div>
                    <p className="text-sm text-navy-600 mt-1">{r.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        <div className="space-y-6">
          <Section title="Compliance">
            {[["PSARA Licence", a.psara_number, a.psara_status === "VALID"], ["GST", a.gst_number, a.gst_verified], ["PAN", a.pan, true]].map(([l, v, ok]) => (
              <div key={l} className="flex items-center justify-between py-2 border-b border-navy-100 last:border-0">
                <div><div className="text-sm font-semibold text-navy-800">{l}</div><div className="text-xs text-navy-500 font-mono">{v || "—"}</div></div>
                {ok ? <FileCheck size={18} className="text-emerald-500" /> : <Clock size={18} className="text-amber-500" />}
              </div>
            ))}
          </Section>
          <Section title="Insurance"><p className="text-sm text-navy-600">{a.insurance}</p></Section>
          <Section title="Performance">
            <div className="space-y-3">
              {[["Customer Rating", `${a.rating}/5`], ["Response Rate", `${a.response_rate}%`], ["Response Time", `${a.response_time_mins} min`], ["Replacement SLA", `${a.replacement_sla_hours} hours`]].map(([l, v]) => (
                <div key={l} className="flex justify-between text-sm"><span className="text-navy-500">{l}</span><span className="font-semibold text-navy-900">{v}</span></div>
              ))}
            </div>
          </Section>
          <div className="rounded-xl bg-brand p-6 text-white text-center">
            <p className="font-heading font-bold text-lg">Interested?</p>
            <p className="text-sm text-white/90 mt-1">Post your requirement to get a quotation from {a.name}.</p>
            <Button className="mt-4 w-full bg-white text-brand hover:bg-navy-900 hover:text-white" onClick={() => navigate("/post-requirement")}>Request Quotation</Button>
          </div>
          <div className="text-center pt-1">
            <ReportDialog agencyId={a.id} agencyName={a.name} />
          </div>
        </div>
      </div>
    </div>
  );
}
